import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { deleteMyAccount } from "@/lib/account.functions";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/konto/sakerhet")({
  component: SecurityPage,
});

function SecurityPage() {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState("");
  const [deleting, setDeleting] = useState(false);
  const del = useServerFn(deleteMyAccount);
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Minst 6 tecken"); return; }
    if (pwd !== pwd2) { toast.error("Lösenorden matchar inte"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      setPwd(""); setPwd2("");
      toast.success("Lösenord uppdaterat");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte uppdatera");
    } finally {
      setLoading(false);
    }
  }

  async function deleteAccount() {
    if (confirmDel !== "RADERA") { toast.error("Skriv RADERA för att bekräfta"); return; }
    setDeleting(true);
    try {
      await del();
      await qc.cancelQueries();
      qc.clear();
      await supabase.auth.signOut();
      toast.success("Konto raderat");
      navigate({ to: "/", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte radera");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={changePassword} className="rounded-3xl border border-border bg-card p-6 md:p-8 elev-shadow">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-accent/10 text-accent"><KeyRound size={16} /></span>
          <h2 className="font-display text-2xl">Byt lösenord</h2>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <input
            type="password" placeholder="Nytt lösenord" minLength={6} required value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <input
            type="password" placeholder="Bekräfta lösenord" minLength={6} required value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>
        <button
          disabled={loading}
          className="mt-6 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? "Sparar…" : "Uppdatera lösenord"}
        </button>
      </form>

      <div className="rounded-3xl border border-destructive/40 bg-destructive/5 p-6 md:p-8">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-destructive/10 text-destructive"><Trash2 size={16} /></span>
          <h2 className="font-display text-2xl">Radera konto</h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Detta tar bort ditt konto permanent tillsammans med profil, campingtips, kommentarer och historik.
          Åtgärden går inte att ångra. Skriv <strong>RADERA</strong> nedan och klicka på knappen.
        </p>
        <input
          value={confirmDel}
          onChange={(e) => setConfirmDel(e.target.value)}
          placeholder="Skriv RADERA"
          className="mt-4 w-full max-w-xs rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-destructive focus:ring-2 focus:ring-destructive/20"
        />
        <button
          onClick={deleteAccount}
          disabled={deleting || confirmDel !== "RADERA"}
          className="mt-4 block rounded-full bg-destructive px-6 py-3 text-sm font-medium text-destructive-foreground transition hover:bg-destructive/90 disabled:opacity-40"
        >
          {deleting ? "Raderar…" : "Radera mitt konto"}
        </button>
      </div>
    </div>
  );
}
